import { useState, useEffect, useCallback } from 'react'
import { Eye, RefreshCw, Plus, X, TrendingUp, ExternalLink } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

export default function WatchlistPage() {
  const [entities, setEntities] = useState<any[]>([])
  const [newEntity, setNewEntity] = useState('')
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/watchlist')
      setEntities(data.entities || [])
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const addEntity = async () => {
    if (!newEntity.trim()) return
    await api.post('/api/watchlist/add', { name: newEntity.trim() })
    setNewEntity('')
    fetchData()
  }

  const removeEntity = async (name: string) => {
    await api.delete('/api/watchlist/remove', { params: { name } })
    fetchData()
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Eye size={20} className="text-blue" /> Watchlist
          </h1>
          <p className="text-[10px] text-text-secondary">Track specific companies and technologies — 7-day mention counts from 150+ feeds</p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Add entity */}
      <div className="flex gap-2">
        <input value={newEntity} onChange={e => setNewEntity(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') addEntity() }}
          placeholder="Add entity (e.g. Kubernetes, Anthropic, TSMC...)"
          className="input py-2 text-xs flex-1 max-w-sm" />
        <button type="button" onClick={addEntity} className="btn-primary text-xs">
          <Plus size={12} /> Add
        </button>
      </div>

      {loading && entities.length === 0 && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
        </div>
      )}

      {/* Entity cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {entities.map(entity => (
          <div key={entity.name} className="card p-4 hover:border-border-light transition-colors">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-text-primary">{entity.name}</h3>
              <button type="button" onClick={() => removeEntity(entity.name)} className="text-text-muted hover:text-rose transition-colors" title="Remove">
                <X size={12} />
              </button>
            </div>
            <div className="flex items-center gap-4 mb-2">
              <div>
                <span className="text-2xl font-bold text-text-primary">{entity.mentions_7d}</span>
                <span className="text-[10px] text-text-muted ml-1">mentions</span>
              </div>
              <div>
                <span className="text-sm font-bold text-text-secondary">{entity.sources}</span>
                <span className="text-[10px] text-text-muted ml-1">sources</span>
              </div>
            </div>
            {entity.latest && (
              <div className="border-t border-border pt-2">
                <p className="text-[10px] text-text-secondary leading-snug truncate">
                  {entity.latest.url
                    ? <a href={entity.latest.url} target="_blank" rel="noopener" className="hover:underline">{entity.latest.title}</a>
                    : entity.latest.title}
                </p>
                <span className="text-[9px] text-text-muted">{entity.latest.source} &middot; {relativeTime(entity.latest.timestamp)}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
